import {
	CheckboxField,
	CreateDrawerFormProps,
	DateField,
	FormDrawer,
	FormDrawerContent,
	FormDrawerContentProps,
	FormFetchDrawer,
	TextField,
	UpdateDrawerFormProps,
} from "@dgs/core";
import { FormikErrors } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ContactFilterSelectField } from "../contactFilter/ContactFilterSelectField";
import { contactUpdateInquiryService } from "../shared/api/contactUpdateInquiryService";
import { ConsentMultiSelect } from "../shared/select/ConsentMultiSelect";
import { DataFieldMultiSelect } from "../shared/select/DataFieldMultiSelect";
import { ContactUpdateInquiryFormValues } from "../shared/types/contactUpdateInquiry";

const useValidate = () => {
	const { t } = useTranslation();

	return (values: ContactUpdateInquiryFormValues) => {
		const errors: FormikErrors<ContactUpdateInquiryFormValues> = {};

		if (!values.name) {
			errors.name = t("Field is required");
		}

		if (!values.startDate) {
			errors.startDate = t("Field is required");
		}

		if (!values.dueDate) {
			errors.dueDate = t("Field is required");
		}

		if (!values.contactFilter) {
			errors.contactFilter = t("Field is required");
		}

		if (values.startDate && values.dueDate && values.startDate > values.dueDate) {
			errors.dueDate = t("Due date needs to be after start date");
		}

		return errors;
	};
};

const ContactUpdateInquiryDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();

	return (
		<FormDrawerContent {...props}>
			<TextField label={t("name")} name="name" required={true} />
			<DateField name="startDate" label={t("Email delivery")} required={true} />
			<ContactFilterSelectField
				label={t("Contact filter")}
				name="contactFilter"
				required={true}
				types={["public", "system"]}
			/>
			<DateField name="dueDate" label={t("Due date")} required={true} />
			<DataFieldMultiSelect name="additionalDataFields" label={t("Additional data fields")} />
			<ConsentMultiSelect name="consents" label={t("Consents")} />
			<CheckboxField label={t("Accept contact updates automatically")} name="acceptContactUpdatesAutomatically" />
			<CheckboxField label={t("Include archived contacts")} name="includeArchivedContacts" />
		</FormDrawerContent>
	);
};

export const ContactUpdateInquiryCreateDrawer: FC<CreateDrawerFormProps<ContactUpdateInquiryFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const initialValues: ContactUpdateInquiryFormValues = {
		acceptContactUpdatesAutomatically: false,
		includeArchivedContacts: false,
		consents: null,
		additionalDataFields: [],
		contactFilter: null,
		dueDate: null,
		startDate: null,
		name: "",
	};

	return (
		<FormDrawer
			{...props}
			initialValues={initialValues}
			title={t("Create contact update campaign")}
			validate={validate}
			renderForm={(props) => <ContactUpdateInquiryDrawerContent {...props} />}
		/>
	);
};

export const ContactUpdateInquiryUpdateDrawer: FC<UpdateDrawerFormProps<ContactUpdateInquiryFormValues>> = (props) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<FormFetchDrawer
			{...props}
			title={t("Update contact update campaign")}
			queryFn={() => contactUpdateInquiryService.show(props.id).then((x) => x.data.data)}
			queryKey={contactUpdateInquiryService.showKey(props.id)}
			validate={validate}
			renderSuccess={(props) => <ContactUpdateInquiryDrawerContent {...props} />}
		/>
	);
};
